import React, { FC, Fragment } from "react";
import { ExternalChatOrganization } from "domain-entities";
import { Box, List, ListItem, makeStyles } from "@material-ui/core";
import { color, px } from "Configs/theme";
import { useTranslation } from "Providers/TranslationProvider";
import OrganizationAvatar from "./OrganizationAvatar";
import { HelpOutline } from "@material-ui/icons";
import { MicroCommunicationEvent, microEventDispatcher } from "@craftnote/micro-communication";
import FadeInBox from "../Layout/FadeInBox";
import { EmptyPartnersIcon } from "../Icons/EmptyParnersIcon";

interface Props {
  organizations: ExternalChatOrganization[];
  setSelectedOrganization: (organization: ExternalChatOrganization) => void;
  unreadOrganizations: string[]
}

const OrganizationsView: FC<Props> = (props) => {
  const classes = useStyles();
  const {t} = useTranslation();

  function dispatchMicroEventForPartnerInfo() {
    microEventDispatcher({
      eventName: MicroCommunicationEvent.EXTERNAL_CHAT_PARTNER_HELP_CLICKED
    })
  }

  function renderOrganizationListItem(organization: ExternalChatOrganization) {
    return (
        <ListItem
            className={classes.listItem}
            onClick={() => props.setSelectedOrganization(organization)}
            button
        >
          <OrganizationAvatar organization={organization}/>
          <Box className={classes.content}>
            <Box className={classes.name}>{organization.name}</Box>
            {organization.location && (
                <Box className={classes.location}>{organization.location.name}</Box>
            )}
          </Box>
          {props.unreadOrganizations.includes(organization.id) && (
              <Box className={classes.unreadIndicator}></Box>
          )}
        </ListItem>
    );
  }

  function renderList() {
    if (props.organizations.length) {
      return (<List className={classes.list}>
        {props.organizations.map((organization, index) => (
            <FadeInBox key={organization.id} delayIndex={index} delay={70}>
              {renderOrganizationListItem(organization)}
            </FadeInBox>
        ))}
      </List>)
    }
  }

  function renderError() {
    if (!props.organizations.length) {
      return (<Box className={classes.errorBox}>
        <Box>
          <Box className={classes.errorBoxIcon}>
            <EmptyPartnersIcon fontSize="large"></EmptyPartnersIcon>
          </Box>
          <Box>
            {t("OrganizationsView_Error_NoOrganizations")}
          </Box>
        </Box>
      </Box>)
    }
  }

  function renderOrganizationList() {
    return (
        <Fragment>
          <Box className={classes.header}>
            <Box className={classes.title}>{t("OrganizationsView_Title")}</Box>
            <HelpOutline className={classes.icon} onClick={dispatchMicroEventForPartnerInfo}></HelpOutline>
          </Box>

          {renderList()}
          {renderError()}
        </Fragment>
    );
  }

  return <Box className={classes.root}>{renderOrganizationList()}</Box>;
};

export default OrganizationsView;

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: px.s,
  },
  title: {
    fontSize: "14px",
    fontWeight: "bold",
    flexGrow: 1
  },
  icon: {
    cursor: "pointer"
  },
  list: {
    padding: px.none,
  },
  listItem: {
    display: "flex",
    backgroundColor: color.card,
    borderRadius: px.xs,
    marginBottom: px.s,
    padding: px.m,
    "&:hover": {
      opacity: "1!important" as "1",
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    paddingLeft: px.m,
    width: 0,
  },
  name: {
    fontSize: "14px",
    fontWeight: "bold",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    marginBottom: px.xs,
  },
  location: {
    fontSize: "12px",
    fontWeight: "bold",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    opacity: 0.5,
  },
  unreadIndicator: {
    backgroundColor: color.primary,
    marginLeft: px.s,
    borderRadius: px.m,
    minWidth: px.m,
    height: px.m,
    alignSelf: "end"
  },
  errorBox: {
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: color.grayBackground01,
  },
  errorBoxIcon: {
    opacity: "0.5",
    marginBottom: px.s
  }
});
